// React hook for forecast project management

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ForecastProject } from '@/types/forecast';
import { forecastApi } from '@/services/api/forecastApi';
import { useCurrentUserId } from '@/hooks/useCurrentUserId';
import { createLogger } from '@/utils/logger';

// Define context types for mutations
interface MutationContext {
  previousForecasts: unknown;
}

interface UseForecastProjectResult {
  // Data
  forecastProjects: ForecastProject[];
  
  // Loading states
  isLoading: boolean;
  isLoadingList: boolean;
  
  // Mutations
  saveForecastProject: ReturnType<typeof useMutation<ForecastProject, Error, ForecastProject, MutationContext>>;
  updateForecastProject: ReturnType<typeof useMutation<ForecastProject, Error, { id: string; updates: Partial<ForecastProject> }, MutationContext>>;
  deleteForecastProject: ReturnType<typeof useMutation<void, Error, string, MutationContext>>;
  
  // Actions
  invalidateForecasts: () => void;
}

// Query keys for React Query caching
export const FORECAST_QUERY_KEYS = {
  all: ['forecasts'] as const,
  lists: () => [...FORECAST_QUERY_KEYS.all, 'list'] as const,
  details: () => [...FORECAST_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...FORECAST_QUERY_KEYS.details(), id] as const,
};

const logger = createLogger('ForecastProject');

/**
 * Hook for managing forecast project operations
 */
export function useForecastProject(): UseForecastProjectResult {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();
  
  // List forecast projects
  const {
    data: forecastsResponse,
    isLoading: isLoadingList
  } = useQuery({
    queryKey: FORECAST_QUERY_KEYS.lists(),
    queryFn: () => forecastApi.listForecasts(),
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const forecastProjects = forecastsResponse?.forecasts || [];

  // Save forecast project mutation
  const saveForecastProject = useMutation<ForecastProject, Error, ForecastProject, MutationContext>({
    mutationFn: async (workspace: ForecastProject) => {
      logger.debug('Saving forecast project:', workspace.name);
      return forecastApi.saveForecast(workspace, userId);
    },
    onMutate: async (newForecast) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: FORECAST_QUERY_KEYS.lists() });
      
      // Snapshot previous value
      const previousForecasts = queryClient.getQueryData(FORECAST_QUERY_KEYS.lists());
      
      // Optimistically update the cache
      queryClient.setQueryData(FORECAST_QUERY_KEYS.lists(), (old: unknown) => {
        if (!old) return { forecasts: [newForecast], total: 1 };
        
        const oldData = old as { forecasts: ForecastProject[]; total: number };
        return {
          forecasts: [newForecast, ...oldData.forecasts],
          total: oldData.total + 1
        };
      });
      
      return { previousForecasts };
    },
    onError: (err, _newForecast, context) => {
      logger.error('Error saving forecast:', err);

      // Rollback optimistic update
      if (context?.previousForecasts) {
        queryClient.setQueryData(FORECAST_QUERY_KEYS.lists(), context.previousForecasts);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: FORECAST_QUERY_KEYS.lists() });
    },
  });

  // Update forecast project mutation
  const updateForecastProject = useMutation<ForecastProject, Error, { id: string; updates: Partial<ForecastProject> }, MutationContext>({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ForecastProject> }) => {
      logger.debug('Updating forecast project:', id);
      return forecastApi.updateForecast(id, updates, userId);
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: FORECAST_QUERY_KEYS.lists() });
      
      const previousForecasts = queryClient.getQueryData(FORECAST_QUERY_KEYS.lists());
      
      // Optimistically update
      queryClient.setQueryData(FORECAST_QUERY_KEYS.lists(), (old: unknown) => {
        if (!old) return old;
        
        const oldData = old as { forecasts: ForecastProject[]; total: number };
        return {
          ...oldData,
          forecasts: oldData.forecasts.map((forecast: ForecastProject) =>
            forecast.id === id ? { ...forecast, ...updates } : forecast
          )
        };
      });
      
      return { previousForecasts };
    },
    onError: (err, _variables, context) => {
      logger.error('Error updating forecast:', err);
      if (context?.previousForecasts) {
        queryClient.setQueryData(FORECAST_QUERY_KEYS.lists(), context.previousForecasts);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FORECAST_QUERY_KEYS.lists() });
    },
  });

  // Delete forecast project mutation
  const deleteForecastProject = useMutation<void, Error, string, MutationContext>({
    mutationFn: async (id: string) => {
      logger.debug('Deleting forecast project:', id);
      return forecastApi.deleteForecast(id);
    },
    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey: FORECAST_QUERY_KEYS.lists() });
      
      const previousForecasts = queryClient.getQueryData(FORECAST_QUERY_KEYS.lists());
      
      // Optimistically remove from cache
      queryClient.setQueryData(FORECAST_QUERY_KEYS.lists(), (old: unknown) => {
        if (!old) return old;
        
        const oldData = old as { forecasts: ForecastProject[]; total: number };
        return {
          forecasts: oldData.forecasts.filter((forecast: ForecastProject) => forecast.id !== deletedId),
          total: oldData.total - 1
        };
      });
      
      return { previousForecasts };
    },
    onError: (err, _deletedId, context) => {
      logger.error('Error deleting forecast:', err);
      if (context?.previousForecasts) {
        queryClient.setQueryData(FORECAST_QUERY_KEYS.lists(), context.previousForecasts);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FORECAST_QUERY_KEYS.lists() });
    },
  });

  // Helper to invalidate all forecast queries
  const invalidateForecasts = () => {
    queryClient.invalidateQueries({ queryKey: FORECAST_QUERY_KEYS.all });
  };

  // Combined loading state
  const isLoading = saveForecastProject.isPending ||
                   updateForecastProject.isPending ||
                   deleteForecastProject.isPending;

  return {
    // Data
    forecastProjects,
    
    // Loading states
    isLoading,
    isLoadingList,
    
    // Mutations
    saveForecastProject,
    updateForecastProject,
    deleteForecastProject,
    
    // Actions
    invalidateForecasts
  };
}

/**
 * Hook for loading a specific forecast project
 */
export function useForecastProjectDetail(id: string | undefined) {
  return useQuery({
    queryKey: FORECAST_QUERY_KEYS.detail(id!),
    queryFn: () => forecastApi.loadForecast(id!),
    enabled: !!id,
    staleTime: 60000,
    cacheTime: 600000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching all forecast projects (used in LoadForecastDialog)
 */
export function useForecastProjects() {
  const { data: forecastsResponse, isLoading, error } = useQuery({
    queryKey: FORECAST_QUERY_KEYS.lists(),
    queryFn: () => forecastApi.listForecasts(),
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  return {
    data: forecastsResponse?.forecasts || [],
    isLoading,
    error
  };
}
